import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setOpenSubmenu(null);
  }, [location]);
  const navLinks = [{
    name: 'Programs',
    path: '/programs'
  }, {
    name: 'Classes',
    path: '/classes',
    submenu: [{
      name: 'Taekwondo',
      path: '/classes/taekwondo'
    }, {
      name: 'Kickboxing',
      path: '/classes/kickboxing'
    }, {
      name: 'Homeschool',
      path: '/classes/homeschool'
    }, {
      name: 'Group & Corporate Self Defense Seminars',
      path: '/classes/self-defense-seminars'
    }, {
      name: 'Private Classes',
      path: '/classes/private-classes'
    }, {
      name: 'Our Classes Schedule',
      path: '/schedule'
    }, {
      name: 'Our Facility',
      path: '/facility'
    }]
  }, {
    name: 'Benefits',
    path: '/child-benefits',
    submenu: [{
      name: 'Child Benefits',
      path: '/child-benefits'
    }, {
      name: 'Teen Benefits',
      path: '/teen-benefits'
    }, {
      name: 'Adult Benefits',
      path: '/adult-benefits'
    }, {
      name: 'Family Benefits',
      path: '/family-benefits'
    }, {
      name: 'Personal Safety',
      path: '/personal-safety'
    }]
  }, {
    name: 'Midwest Cup 2026',
    path: '/midwest-cup-2026'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <nav className="fixed top-0 left-0 right-0 z-50 bg-black shadow-lg py-2 transition-all duration-300" data-id="element-203">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-204">
        <div className="flex justify-between items-center" data-id="element-205">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-id="element-206">
            <div className="relative w-16 h-16 overflow-hidden rounded-full border-2 border-[#00D4FF] bg-black" data-id="element-207">
              <img src="https://cdn.magicpatterns.com/uploads/fmBXjQm2BPtC5bTSRKvwCz/walkerlogo-gigapixel-cgi-3600w.png" alt="Walker's Martial Arts Logo" className="w-full h-full object-cover" data-id="element-208" />
            </div>
            <div className="flex flex-col" data-id="element-209">
              <span className="text-white font-bold text-xl leading-none tracking-wide uppercase" data-id="element-210">
                Walker's
              </span>
              <span className="text-[#00D4FF] font-bold text-base leading-none tracking-wider uppercase" data-id="element-211">
                Martial Arts
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8" data-id="element-212">
            {navLinks.map(link => <div key={link.name} className="relative group" data-id="element-213">
                {link.submenu ? <>
                    <button className={`transition-colors font-medium text-sm uppercase tracking-wider flex items-center gap-1 ${link.submenu.some(sub => sub.path === location.pathname) ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-214">
                      {link.name}
                      <ChevronDown className="w-4 h-4" data-id="element-215" />
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-0 mt-2 w-48 bg-black/95 backdrop-blur-sm border border-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" data-id="element-216">
                      {link.submenu.map(sublink => <Link key={sublink.path} to={sublink.path} className={`block px-4 py-3 text-sm font-medium transition-colors ${location.pathname === sublink.path ? 'text-[#00D4FF] bg-[#00D4FF]/10' : 'text-gray-300 hover:text-[#00D4FF] hover:bg-[#00D4FF]/5'}`} data-id="element-217">
                          {sublink.name}
                        </Link>)}
                    </div>
                  </> : <Link to={link.path} className={`transition-colors font-medium text-sm uppercase tracking-wider ${location.pathname === link.path ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-218">
                    {link.name}
                  </Link>}
              </div>)}
            <Link to="/free-trial" className="bg-[#00D4FF] text-black px-6 py-2.5 rounded font-bold uppercase tracking-wider text-sm hover:bg-[#33ddff] transition-colors shadow-[0_0_15px_rgba(0,212,255,0.3)] hover:shadow-[0_0_25px_rgba(0,212,255,0.5)]" data-id="element-219">
              Free Trial
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsOpen(true)} className="md:hidden text-white p-2 hover:text-[#00D4FF] transition-colors" aria-label="Open menu" data-id="element-220">
            <Menu size={28} data-id="element-221" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence data-id="element-222">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden" data-id="element-223" />

            {/* Drawer */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-[280px] bg-[#1a1a1a] z-50 shadow-2xl border-l border-gray-800 flex flex-col" data-id="element-224">
              <div className="p-6 flex justify-between items-center border-b border-gray-800" data-id="element-225">
                <span className="text-white font-bold text-lg" data-id="element-226">Menu</span>
                <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors" data-id="element-227">
                  <X size={24} data-id="element-228" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-6 px-6 flex flex-col gap-4" data-id="element-229">
                <Link to="/" onClick={() => setIsOpen(false)} className="text-lg font-medium text-gray-300 hover:text-[#00D4FF] transition-colors" data-id="element-230">
                  Home
                </Link>
                {navLinks.map(link => <div key={link.name} data-id="element-231">
                    {link.submenu ? <>
                        <button onClick={() => setOpenSubmenu(openSubmenu === link.name ? null : link.name)} className={`w-full text-left text-lg font-medium transition-colors flex items-center justify-between ${link.submenu.some(sub => sub.path === location.pathname) ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-232">
                          {link.name}
                          <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === link.name ? 'rotate-180' : ''}`} data-id="element-233" />
                        </button>
                        <AnimatePresence data-id="element-234">
                          {openSubmenu === link.name && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-235">
                              <div className="pl-4 pt-2 space-y-2" data-id="element-236">
                                {link.submenu.map(sublink => <Link key={sublink.path} to={sublink.path} onClick={() => setIsOpen(false)} className={`block py-2 text-sm font-medium transition-colors ${location.pathname === sublink.path ? 'text-[#00D4FF]' : 'text-gray-400 hover:text-[#00D4FF]'}`} data-id="element-237">
                                    {sublink.name}
                                  </Link>)}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </> : <Link to={link.path} onClick={() => setIsOpen(false)} className={`text-lg font-medium transition-colors block ${location.pathname === link.path ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'}`} data-id="element-238">
                        {link.name}
                      </Link>}
                  </div>)}
                <hr className="border-gray-800 my-2" data-id="element-239" />
                <Link to="/free-trial" onClick={() => setIsOpen(false)} className="bg-[#00D4FF] text-black py-3 rounded text-center font-bold uppercase tracking-wider hover:bg-[#33ddff] transition-colors block" data-id="element-240">
                  Schedule Free Trial
                </Link>

                <div className="mt-auto pt-8" data-id="element-241">
                  <div className="flex items-center gap-3 text-gray-400 mb-2" data-id="element-242">
                    <Phone size={18} className="text-[#00D4FF]" data-id="element-243" />
                    <span className="text-sm" data-id="element-244">Call us today</span>
                  </div>
                  <p className="text-white font-medium text-lg" data-id="element-245">
                    Bloomington, IL
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}